/**
 * ReportQueue — paginated list of report drafts with status counters + filters.
 * Click-through opens the ReportEditor.
 */

import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileText, Search, Filter, RefreshCw, AlertCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
} from '../ui';
import {
  getReports,
  getStatusLabel,
  getStatusColor,
  getReportTypeLabel,
} from '../../services/reports';
import type { ReportSummary, ReportStatus } from '../../data/types';

interface ReportQueueProps {
  onSelect: (reportId: string) => void;
}

export function ReportQueue({ onSelect }: ReportQueueProps) {
  const [reports, setReports] = useState<ReportSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    const filters =
      statusFilter !== 'all'
        ? { status: statusFilter, limit: 100 }
        : { limit: 100 };
    const result = await getReports(filters);
    if (result.success) {
      setReports(result.data.reports);
      setTotal(result.data.pagination.total);
    } else {
      setError(result.error ?? 'Failed to load reports');
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Counts by status — drives the headline stats row.
  const counts = useMemo(() => {
    const c: Record<string, number> = {
      generating: 0,
      draft: 0,
      under_review: 0,
      approved: 0,
      rejected: 0,
      sent: 0,
    };
    (reports ?? []).forEach((r) => {
      c[r.status] = (c[r.status] ?? 0) + 1;
    });
    return c;
  }, [reports]);

  const filtered = useMemo(() => {
    if (!reports) return [];
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter(
      (r) =>
        r.reportTitle.toLowerCase().includes(q) ||
        r.tenantDisplayName.toLowerCase().includes(q),
    );
  }, [reports, search]);

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Pending Review" value={counts.draft} />
        <StatCard label="Under Review" value={counts.under_review} />
        <StatCard label="Published" value={counts.sent} />
        <StatCard label="Rejected" value={counts.rejected} />
      </div>

      {/* Search + filter row */}
      <Card>
        <CardContent>
          <div className="flex items-center gap-3 p-2">
            <div className="flex-1 flex items-center gap-2 bg-bg-secondary rounded px-3 py-2">
              <Search className="w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search reports..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
            >
              <Filter className="w-4 h-4 mr-1" />
              Filters
            </Button>
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {showFilters && (
            <div className="px-2 pb-2">
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  active={statusFilter === 'all'}
                  onClick={() => setStatusFilter('all')}
                >
                  All
                </FilterChip>
                {(
                  ['draft', 'under_review', 'approved', 'rejected', 'sent', 'generating'] as ReportStatus[]
                ).map((s) => (
                  <FilterChip
                    key={s}
                    active={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                  >
                    {getStatusLabel(s)}
                  </FilterChip>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent>
            <div className="py-8 text-center text-sm text-red-600">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              {error}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent>
            <div className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-text-tertiary mb-3" />
              <h3 className="text-base font-medium text-text-primary mb-1">
                No reports found
              </h3>
              <p className="text-sm text-text-secondary">
                {total === 0
                  ? 'No reports have been generated yet.'
                  : 'No reports match your filters.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((report) => (
            <button
              key={report.reportId}
              onClick={() => onSelect(report.reportId)}
              className="w-full text-left"
            >
              <Card className="hover:bg-bg-secondary transition-colors">
                <CardContent>
                  <div className="flex items-center justify-between gap-3 p-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-text-tertiary mt-1 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold text-text-primary truncate">
                            {report.reportTitle}
                          </span>
                          <Badge variant={getStatusColor(report.status)}>
                            {getStatusLabel(report.status)}
                          </Badge>
                        </div>
                        <div className="text-xs text-text-secondary">
                          {report.tenantDisplayName} ·{' '}
                          {getReportTypeLabel(report.reportType)} ·{' '}
                          {format(parseISO(report.periodStart), 'd MMM')} →{' '}
                          {format(parseISO(report.periodEnd), 'd MMM yyyy')}
                        </div>
                        <div className="text-xs text-text-tertiary mt-1">
                          Generated {report.generatedBy ?? 'unknown'} ·{' '}
                          {format(parseISO(report.generatedAt), 'd MMM HH:mm')}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <div className="p-3">
          <div className="text-3xl font-bold text-text-primary">{value}</div>
          <div className="text-xs text-text-secondary mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? 'bg-brand-navy text-white border-brand-navy dark:bg-brand-lime dark:text-brand-navy dark:border-brand-lime'
          : 'bg-bg-primary text-text-secondary border-border hover:bg-bg-secondary'
      }`}
    >
      {children}
    </button>
  );
}
